import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal } from '../../../components/Modal/Modal';
import { Button } from '../../../components/Button/Button';
import { IconButton } from '../../../components/IconButton/IconButton';
import Input from '../../../components/Input/Input';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';
import { AnalysisTreePanel } from '../../../components/AnalysisTreePanel/AnalysisTreePanel';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../context/ToastContext';
import { useAuth } from '../../../context/AuthContext';
import FmeaAiagVdaApi from '../../../services/FmeaAiagVdaApi';
import {
  FailureAnalysisTreeDto,
  FailureAnalysisItemNode,
  FailureAnalysisStepNode,
  FailureAnalysisElementNode,
  FailureAnalysisFunctionDto,
  FailureAnalysisRequirementDto,
  FailureAnalysisEntryDto,
} from '../../../interfaces/FmeaStep4';
import { Cause6MEnum } from '../../../interfaces/FmeaStep2';
import styles from './Step4.module.css';

type NodeSelection =
  | { type: 'item'; itemId: string }
  | { type: 'step'; itemId: string; stepId: string }
  | { type: 'element'; itemId: string; stepId: string; elementId: string };

type SelectionContext =
  | { type: 'item'; item: FailureAnalysisItemNode }
  | { type: 'step'; item: FailureAnalysisItemNode; step: FailureAnalysisStepNode }
  | { type: 'element'; item: FailureAnalysisItemNode; step: FailureAnalysisStepNode; element: FailureAnalysisElementNode };

type FailureTargetContext = {
  item: FailureAnalysisItemNode;
  step: FailureAnalysisStepNode;
  element: FailureAnalysisElementNode;
  fn: FailureAnalysisFunctionDto;
};

const causeIconMap: Record<number, string> = {
  [Cause6MEnum.Man]: 'person',
  [Cause6MEnum.Machine]: 'memory',
  [Cause6MEnum.Method]: 'menu_book',
  [Cause6MEnum.Material]: 'inventory_2',
  [Cause6MEnum.Measurement]: 'straighten',
  [Cause6MEnum.Environment]: 'factory',
};

const getEntityName = (entity: any, fallback: string): string => {
  if (!entity) return fallback;
  return (
    entity.name ??
    entity.title ??
    entity.description ??
    entity.operationName ??
    entity.elementDescription ??
    fallback
  );
};

const computeFailureCount = (functions?: FailureAnalysisFunctionDto[], requirements?: FailureAnalysisRequirementDto[]): number => {
  let count = 0;
  functions?.forEach(fn => { count += fn.failures?.length ?? 0; });
  requirements?.forEach(req => { count += req.failures?.length ?? 0; });
  return count;
};

const hasPendingFailures = (functions?: FailureAnalysisFunctionDto[], requirements?: FailureAnalysisRequirementDto[]): boolean => {
  const fns = functions || [];
  const reqs = requirements || [];
  if (fns.length === 0 && reqs.length === 0) return true;
  const fnsComplete = fns.every(fn => (fn.failures?.length ?? 0) > 0);
  const reqsComplete = reqs.every(req => (req.failures?.length ?? 0) > 0);
  return !fnsComplete || !reqsComplete;
};

const mapToTreeNodes = (
  items: FailureAnalysisItemNode[],
  _translate: (k: string) => string
): any[] => {
  const getBadges = (functions?: FailureAnalysisFunctionDto[], requirements?: FailureAnalysisRequirementDto[]): React.ReactNode => (
    <div className={styles.badgeGroup}>
      <span className={styles.badge}>{`F: ${functions?.length ?? 0}`}</span>
      <span className={styles.badge}>{`X: ${computeFailureCount(functions, requirements)}`}</span>
    </div>
  );

  const getStatusIcon = (functions?: FailureAnalysisFunctionDto[], requirements?: FailureAnalysisRequirementDto[]): React.ReactNode => {
    const pending = hasPendingFailures(functions, requirements);
    return (
      <span className={`${styles.statusIcon} ${!pending ? styles.statusComplete : ''}`} title={!pending ? "Concluído" : "Dados pendentes"}>
        <i className="material-symbols-outlined">{!pending ? 'check_circle' : 'warning'}</i>
      </span>
    );
  };

  return items.map(item => ({
    id: item.id,
    label: getEntityName(item, 'Item'),
    type: 'item',
    icon: 'view_column',
    badges: getBadges(item.functions, item.requirements),
    statusIcon: getStatusIcon(item.functions, item.requirements),
    children: (item.steps || []).map(step => ({
      id: step.id,
      label: getEntityName(step, 'Etapa'),
      type: 'step',
      icon: 'settings',
      badges: getBadges(step.functions, step.requirements),
      statusIcon: getStatusIcon(step.functions, step.requirements),
      children: (step.elements || []).map(el => ({
        id: el.id,
        label: getEntityName(el, 'Elemento'),
        type: 'element',
        icon: el.cause6m != null ? causeIconMap[el.cause6m] : 'category',
        badges: getBadges(el.functions, el.requirements),
        statusIcon: getStatusIcon(el.functions, el.requirements),
      }))
    }))
  }));
};

const Step4: React.FC = () => {
  const { fmeaId } = useParams<{ fmeaId: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { userId } = useAuth();
  const { t: translate } = useTranslation('form');

  const [tree, setTree] = useState<FailureAnalysisTreeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNode, setSelectedNode] = useState<NodeSelection | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  const [showFailureModal, setShowFailureModal] = useState(false);
  const [currentTarget, setCurrentTarget] = useState<FailureTargetContext | null>(null);
  const [editingFailure, setEditingFailure] = useState<FailureAnalysisEntryDto | null>(null);
  const [failureForm, setFailureForm] = useState({ effect: '', mode: '', causeDescription: '' });

  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; title: string; message: string; onAccept: () => void } | null>(null);

  const loadTree = useCallback(async (search?: string) => {
    if (!fmeaId) return;
    try {
      setLoading(true);
      const data = await FmeaAiagVdaApi.GetStep4FailureAnalysis(fmeaId, search);
      setTree(data);
      if (data.completed) setReadOnly(true);
    } catch (err) {
      displayToast('error', translate('error'), 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [fmeaId, displayToast, translate]);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const selectedContext = useMemo((): SelectionContext | null => {
    if (!selectedNode || !tree) return null;
    const item = tree.items.find(i => i.id === selectedNode.itemId);
    if (!item) return null;
    if (selectedNode.type === 'item') return { type: 'item', item };
    const step = (item.steps || []).find(s => s.id === selectedNode.stepId);
    if (!step) return null;
    if (selectedNode.type === 'step') return { type: 'step', item, step };
    const element = (step.elements || []).find(e => e.id === selectedNode.elementId);
    if (!element) return null;
    return { type: 'element', item, step, element };
  }, [selectedNode, tree]);

  const handleAddFailure = (fn: FailureAnalysisFunctionDto): void => {
    if (!selectedContext) return;
    const target: FailureTargetContext = {
      item: selectedContext.item,
      step: (selectedContext as any).step,
      element: (selectedContext as any).element,
      fn,
    };
    setCurrentTarget(target);
    setEditingFailure(null);
    setFailureForm({ effect: '', mode: '', causeDescription: '' });
    setShowFailureModal(true);
  };

  const handleEditFailure = (fn: FailureAnalysisFunctionDto, failure: FailureAnalysisEntryDto): void => {
    if (!selectedContext) return;
    const target: FailureTargetContext = {
      item: selectedContext.item,
      step: (selectedContext as any).step,
      element: (selectedContext as any).element,
      fn,
    };
    setCurrentTarget(target);
    setEditingFailure(failure);
    setFailureForm({ effect: failure.effect, mode: failure.mode, causeDescription: failure.causeDescription || '' });
    setShowFailureModal(true);
  };

  const saveFailure = async (): Promise<void> => {
    if (!fmeaId || !currentTarget || readOnly) return;
    try {
      const payload = {
        functionId: currentTarget.fn.id,
        effect: failureForm.effect,
        mode: failureForm.mode,
        causeDescription: failureForm.causeDescription,
        createdBy: userId,
      };

      if (editingFailure) {
        await FmeaAiagVdaApi.UpdateFailureAnalysisEntry(fmeaId, editingFailure.id, {
          ...payload,
          updatedBy: userId,
        });
        displayToast('success', translate('success'), 'Falha atualizada');
      } else {
        await FmeaAiagVdaApi.CreateFailureAnalysisEntry(fmeaId, payload);
        displayToast('success', translate('success'), 'Falha adicionada');
      }
      setShowFailureModal(false);
      loadTree(searchTerm);
    } catch (err) {
      displayToast('error', translate('error'), 'Erro ao salvar falha');
    }
  };

  const deleteFailure = (id: string): void => {
    setConfirmModal({
      visible: true,
      title: translate('delete') || 'Excluir',
      message: 'Excluir esta falha?',
      onAccept: async () => {
        try {
          await FmeaAiagVdaApi.DeleteFailureAnalysisEntry(fmeaId!, id);
          displayToast('success', translate('success'), 'Falha excluída');
          loadTree(searchTerm);
          setConfirmModal(null);
        } catch (err) {
          displayToast('error', translate('error'), 'Erro ao excluir');
        }
      }
    });
  };

  const handleComplete = async (): Promise<void> => {
    if (!fmeaId || readOnly) {
        if (readOnly) navigate(`/fmeas/${fmeaId}/steps/5`);
        return;
    }
    setCompleting(true);
    try {
      const validation = await FmeaAiagVdaApi.ValidateFailureAnalysis(fmeaId);
      if (!validation?.valid) {
        setPendingMessage(validation?.message || 'Existem falhas pendentes na estrutura.');
        displayToast('warn', translate('attention'), validation?.message || 'Atenção');
        return;
      }
      const result = await FmeaAiagVdaApi.CompleteFailureAnalysis(fmeaId, userId || '');
      if (result?.completed) {
        displayToast('success', translate('success'), 'Etapa concluída');
        navigate(`/fmeas/${fmeaId}/steps/5`);
      }
    } catch (err) {
       displayToast('error', translate('error'), 'Erro ao finalizar');
    } finally {
      setCompleting(false);
    }
  };

  const renderFunctions = (): React.ReactNode => {
    if (!selectedContext) return null;
    const functions = selectedContext.type === 'item' ? selectedContext.item.functions : selectedContext.type === 'step' ? selectedContext.step.functions : selectedContext.element.functions;
    if (!functions?.length) return <p className={styles.emptyText}>Nenhuma função vinculada.</p>;

    return functions.map(fn => (
      <div key={fn.id} className={styles.functionCard}>
        <div className={styles.functionHeader}>
          <span className={styles.functionTitle}>{fn.description}</span>
          {!readOnly && (
            <IconButton icon="add" variant="text" severity="success" aria-label="Adicionar falha" onClick={() => handleAddFailure(fn)} />
          )}
        </div>
        <div className={styles.failureList}>
          {(fn.failures || []).map(f => (
            <div key={f.id} className={styles.failureItem}>
              <div className={styles.failureContent}>
                <p><strong>Modo:</strong> {f.mode}</p>
                <p><strong>Efeito:</strong> {f.effect}</p>
                {f.causeDescription && <p><strong>Causa:</strong> {f.causeDescription}</p>}
              </div>
              {!readOnly && (
                <div className={styles.failureActions}>
                   <IconButton icon="edit" variant="text" severity="neutral" aria-label="Editar" onClick={() => handleEditFailure(fn, f)} />
                   <IconButton icon="delete" variant="text" severity="danger" aria-label="Excluir" onClick={() => deleteFailure(f.id)} />
                </div>
              )}
            </div>
          ))}
          {fn.failures?.length === 0 && <p className={styles.hintText}>Adicione pelo menos uma falha para esta função.</p>}
        </div>
      </div>
    ));
  };

  return (
    <>
      <StepWrapper
        title="Análise de Falhas (AIAG-VDA)"
        description="Identifique os modos de falha, efeitos e causas para cada função da sua estrutura."
        currentStep={4}
        onPrevious={() => navigate(`/fmeas/${fmeaId}/steps/3`)}
        onNext={handleComplete}
        nextDisabled={completing}
        nextLoading={completing}
      >
        <AnalysisTreePanel
          masterTitle="Estrutura de Falhas"
          nodes={useMemo(() => mapToTreeNodes(tree?.items || [], translate), [tree, translate])}
          selectedNodeId={selectedNode?.type === 'element' ? selectedNode.elementId : selectedNode?.type === 'step' ? selectedNode.stepId : selectedNode?.itemId}
          onSelectNode={(id, type) => {
            if (type === 'item') setSelectedNode({ type: 'item', itemId: id });
            else if (type === 'step') {
              const item = tree?.items.find(i => (i.steps || []).some(s => s.id === id));
              if (item) setSelectedNode({ type: 'step', itemId: item.id, stepId: id });
            } else if (type === 'element') {
              const item = tree?.items.find(i => (i.steps || []).some(s => (s.elements || []).some(e => e.id === id)));
              const step = item?.steps?.find(s => (s.elements || []).some(e => e.id === id));
              if (item && step) setSelectedNode({ type: 'element', itemId: item.id, stepId: step.id, elementId: id });
            }
          }}
          searchLabel="Buscar na estrutura"
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Buscar por item, etapa ou elemento"
          loading={loading}
          validationMessage={pendingMessage}
          detailContent={
            selectedContext ? (
               <div className={styles.detailWrapper}>
                 <div className={styles.detailHeader}>
                    <h1 className={styles.detailTitle}>
                      {getEntityName(selectedContext.type === 'item' ? selectedContext.item : selectedContext.type === 'step' ? selectedContext.step : (selectedContext as any).element, 'Registro')}
                    </h1>
                 </div>
                 <div className={styles.contentList}>{renderFunctions()}</div>
               </div>
            ) : null
          }
          emptySelectionMessage="Selecione um registro para analisar falhas."
        />
      </StepWrapper>

      <Modal
        visible={showFailureModal}
        onHide={() => setShowFailureModal(false)}
        header={editingFailure ? 'Editar Falha' : 'Adicionar Falha'}
        footer={
          <div className="flex gap-2">
            <Button label="Cancelar" variant="outlined" severity="secondary" onClick={() => setShowFailureModal(false)} />
            <Button label="Salvar" severity="success" onClick={saveFailure} disabled={!failureForm.mode || !failureForm.effect} />
          </div>
        }
      >
        <div className={styles.modalContent}>
          <p><strong>Função:</strong> {currentTarget?.fn.description}</p>
          <Input type="text" label="Modo de Falha" value={failureForm.mode} onChange={v => setFailureForm((p: any) => ({ ...p, mode: v }))} />
          <Input type="textArea" label="Efeito da Falha" value={failureForm.effect} onChange={v => setFailureForm((p: any) => ({ ...p, effect: v }))} />
          <Input type="textArea" label="Causa da Falha (Opcional)" value={failureForm.causeDescription} onChange={v => setFailureForm((p: any) => ({ ...p, causeDescription: v }))} />
        </div>
      </Modal>

      {confirmModal && (
        <Modal
          visible={confirmModal.visible}
          onHide={() => setConfirmModal(null)}
          header={confirmModal.title}
          footer={
            <div className="flex gap-2">
              <Button label="Não" variant="outlined" severity="secondary" onClick={() => setConfirmModal(null)} />
              <Button label="Sim" severity="danger" onClick={confirmModal.onAccept} />
            </div>
          }
        >
          <p>{confirmModal.message}</p>
        </Modal>
      )}
    </>
  );
};

export default Step4;
